import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
import Quill, { Range } from "quill";
import "quill/dist/quill.snow.css";
import "./PaziaQuill.css";

// SoftBreak blot for line breaks - Quill 2.0 compatible
const Embed = Quill.import("blots/embed") as typeof import("parchment").EmbedBlot;
// === Image with alignment (data-align="left|right|center") ===
const BaseImage = Quill.import("formats/image") as any;

class AlignedImage extends BaseImage {
  static blotName = "image";
  static tagName = "IMG";

  static formats(domNode: HTMLElement) {
    const formats: Record<string, unknown> = (BaseImage.formats?.(domNode) as Record<string, unknown>) || {};
    const align = domNode.getAttribute("data-align");
    if (align) formats.imageAlign = align;
    const width = domNode.getAttribute("width");
    if (width) formats.imageWidth = width;
    return formats;
  }

  format(name: string, value: unknown) {
    if (name === "imageAlign") {
      if (value) this.domNode.setAttribute("data-align", String(value));
      else this.domNode.removeAttribute("data-align");
    } else if (name === "imageWidth") {
      if (value) this.domNode.setAttribute("width", String(value));
      else this.domNode.removeAttribute("width");
    } else {
      super.format(name, value);
    }
  }
}

Quill.register({ "formats/image": AlignedImage }, true);
class SoftBreak extends Embed {
  static blotName = "soft-break";
  static tagName = "BR";
  static className = "ql-soft-break";

  static create(): HTMLElement {
    const node = super.create() as HTMLElement;
    node.setAttribute("data-break", "soft");
    return node;
  }

  static formats(): boolean {
    return true;
  }

  static match(node: HTMLElement): boolean {
    return node.tagName === "BR" && node.hasAttribute("data-break");
  }

  length(): number {
    return 1;
  }

  deleteAt(): void {
    this.remove();
  }

  optimize(): void {
    // Simplified optimization to prevent loops
    if (!this.domNode.parentNode) {
      this.remove();
    }
  }

  value(): string {
    return "\n";
  }
}

// Register SoftBreak blot
Quill.register("formats/soft-break", SoftBreak, true);

// Register fonts
const Font = Quill.import("formats/font") as { whitelist: string[] };
Font.whitelist = ["montserrat", "impact", "monospace", "poppins"] as string[];
// @ts-ignore
Quill.register(Font, true);

// Configure custom colors for unlimited color selection
const ColorStyle = Quill.import("attributors/style/color");
const BackgroundStyle = Quill.import("attributors/style/background");
Quill.register(ColorStyle as string, true);
Quill.register(BackgroundStyle as string, true);

// Shared color palette - used for both text color and background
const COLOR_PALETTE: (string | false)[] = [
  false, // Clear color option
  "#000000",
  "#e60000",
  "#ff9900",
  "#ffff00",
  "#008a00",
  "#0066cc",
  "#9933ff",
  "#ffffff",
  "#facccc",
  "#ffebcc",
  "#ffffcc",
  "#cce8cc",
  "#cce0f5",
  "#ebd6ff",
  "#bbbbbb",
  "#f06666",
  "#ffc266",
  "#ffff66",
  "#66b966",
  "#66a3e0",
  "#c285ff",
  "#888888",
  "#a10000",
  "#b26b00",
  "#b2b200",
  "#006100",
  "#0047b2",
  "#6b24b2",
  "#444444",
  "#5c0000",
  "#663d00",
  "#666600",
  "#003700",
  "#002966",
  "#3d1466",
  "#00ACBF",
  "custom",
];

const modules = {
  table: true,
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: Font.whitelist }],
    [{ size: [] }],
    [{ align: [] }],
    ["bold", "italic", "underline"],
    [{ color: COLOR_PALETTE }, { background: COLOR_PALETTE }],
    [{ script: "super" }, { script: "sub" }],
    ["blockquote", "code-block"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["table"],
    ["clean"],
  ],
  keyboard: {
    bindings: {
      softBreak: {
        key: "Enter",
        shiftKey: true,
        handler: function (this: { quill: Quill }, range: Range): boolean {
          this.quill.insertEmbed(range.index, "soft-break", true, "user");
          this.quill.setSelection(range.index + 1, 0, "silent");
          return false;
        },
      },
    },
  },
};

const formats = [
  "header",
  "font",
  "size",
  "align",
  "bold",
  "italic",
  "underline",
  "color",
  "background",
  "blockquote",
  "code-block",
  "script",
  "list",
  "link",
  "image",
  "soft-break",
  "table",
  // "table-head",
  "table-body",
  // "table-row",
  // "table-cell",
];

interface PaziaQuillProps {
  value?: string;
  onChange?: (content: string) => void;
  readOnly?: boolean;
  placeholder?: string;
}

export interface PaziaQuillHandle {
  getEditor: () => Quill | null;
  focus: () => void;
  blur: () => void;
}

/**
 * Creates a color picker handler that supports preset colors, custom colors, and clear option
 */
function createColorHandler(quill: Quill, formatName: "color" | "background") {
  return function (value: string | false): void {
    if (value === "custom") {
      const input = document.createElement("input");
      input.type = "color";
      input.style.position = "absolute";
      input.style.visibility = "hidden";
      document.body.appendChild(input);

      input.addEventListener("change", () => {
        quill.format(formatName, input.value);
        document.body.removeChild(input);
      });

      // Also clean up if user cancels
      input.addEventListener("blur", () => {
        if (document.body.contains(input)) {
          document.body.removeChild(input);
        }
      });

      input.click();
    } else {
      quill.format(formatName, value);
    }
  };
}

export const PaziaQuill = forwardRef<PaziaQuillHandle, PaziaQuillProps>(
  ({ value = "", onChange, readOnly = false, placeholder }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const onChangeRef = useRef(onChange);
    const initialValueRef = useRef(value);

    // Keep onChange callback up to date
    useLayoutEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useImperativeHandle(ref, () => ({
      getEditor: () => quillRef.current,
      focus: () => quillRef.current?.focus(),
      blur: () => quillRef.current?.blur(),
    }));

    // Initialize Quill editor once
    useEffect(() => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const editorContainer = container.appendChild(container.ownerDocument.createElement("div"));

      const quill = new Quill(editorContainer, {
        modules,
        formats,
        readOnly,
        theme: "snow",
        placeholder: placeholder || "Enter text...",
      });

      quillRef.current = quill;

      // Set initial content
      if (initialValueRef.current) {
        quill.clipboard.dangerouslyPasteHTML(initialValueRef.current);
      }

      // Custom color picker handlers with proper cleanup
      const toolbar = quill.getModule("toolbar") as {
        addHandler: (name: string, handler: (value: string | false) => void) => void;
      };
      toolbar.addHandler("color", createColorHandler(quill, "color"));
      toolbar.addHandler("background", createColorHandler(quill, "background"));
      toolbar.addHandler("table", () => {
        const tableModule = quill.getModule("table") as { insertTable?: (rows: number, cols: number) => void };
        tableModule.insertTable?.(3, 3);
      });

      // Handle text changes
      quill.on("text-change", () => {
        if (onChangeRef.current) {
          onChangeRef.current(quill.root.innerHTML);
        }
      });
      // === Pływający toolbar do wyrównywania obrazków ===
      const imageToolbar = document.createElement("div");
      imageToolbar.className = "pazia-image-toolbar";
      imageToolbar.innerHTML = `
  <button type="button" data-align="left" title="Opływanie z lewej">⬅</button>
  <button type="button" data-align="center" title="Wyśrodkuj">▣</button>
  <button type="button" data-align="right" title="Opływanie z prawej">➡</button>
  <button type="button" data-align="" title="Wyczyść">✕</button>
`;
      document.body.appendChild(imageToolbar);

      let selectedImage: HTMLImageElement | null = null;

      const positionToolbar = (img: HTMLImageElement) => {
        const rect = img.getBoundingClientRect();
        imageToolbar.style.display = "flex";
        imageToolbar.style.left = `${rect.left + window.scrollX}px`;
        imageToolbar.style.top = `${rect.bottom + window.scrollY + 6}px`;
      };

      const hideToolbar = () => {
        imageToolbar.style.display = "none";
        if (selectedImage) {
          selectedImage.classList.remove("ql-image-selected");
          selectedImage = null;
        }
      };

      const onEditorClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "IMG") {
          if (selectedImage) selectedImage.classList.remove("ql-image-selected");
          selectedImage = target as HTMLImageElement;
          selectedImage.classList.add("ql-image-selected");
          positionToolbar(selectedImage);
        } else {
          hideToolbar();
        }
      };

      const onToolbarClick = (e: MouseEvent) => {
        const btn = (e.target as HTMLElement).closest("button");
        if (!btn || !selectedImage) return;
        const value = btn.getAttribute("data-align");
        if (value) {
          selectedImage.setAttribute("data-align", value);
        } else {
          selectedImage.removeAttribute("data-align");
        }
        // przepozycjonuj toolbar bo obrazek mógł zmienić miejsce
        positionToolbar(selectedImage);
        quill.update("user"); // ← to odpala onChange z nowym HTML-em
      };

      const onDocClick = (e: MouseEvent) => {
        // chowaj toolbar gdy klikamy poza edytorem i poza samym toolbarem
        if (!quill.root.contains(e.target as Node) && !imageToolbar.contains(e.target as Node)) {
          hideToolbar();
        }
      };

      quill.root.addEventListener("click", onEditorClick);
      imageToolbar.addEventListener("click", onToolbarClick);
      document.addEventListener("click", onDocClick);

      // === Update cleanup w return ===
      return () => {
        quill.root.removeEventListener("click", onEditorClick);
        imageToolbar.removeEventListener("click", onToolbarClick);
        document.removeEventListener("click", onDocClick);
        imageToolbar.remove();
        quillRef.current = null;
        container.innerHTML = "";
      };
      // Only initialize once - don't re-initialize on readOnly/placeholder changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle readOnly changes without re-initializing
    useEffect(() => {
      if (quillRef.current) {
        quillRef.current.enable(!readOnly);
      }
    }, [readOnly]);

    // Handle placeholder changes without re-initializing
    useEffect(() => {
      if (quillRef.current) {
        quillRef.current.root.dataset.placeholder = placeholder || "Enter text...";
      }
    }, [placeholder]);

    // Handle external value changes
    useEffect(() => {
      if (quillRef.current) {
        const currentContent = quillRef.current.root.innerHTML;
        // Only update if the content is actually different (avoid unnecessary updates)
        if (currentContent !== value && value !== undefined) {
          const selection = quillRef.current.getSelection();
          quillRef.current.clipboard.dangerouslyPasteHTML(value || "");
          // Try to restore selection if possible
          if (selection) {
            quillRef.current.setSelection(selection);
          }
        }
      }
    }, [value]);

    return <div className="pazia-quill-container" ref={containerRef} />;
  },
);

PaziaQuill.displayName = "PaziaQuill";
